import { useQuery } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import type { SearchQueryBody } from "@/types";

/**
 * The spec the convert endpoint parses `query` into, read as a query rather
 * than a mutation so a component can wait on it before it renders. `undefined`
 * disables it.
 *
 * Not retried: a parse failure is deterministic, and three attempts would only
 * delay telling the customer the query is invalid.
 *
 * Not refetched on window focus or reconnect either. A query that failed has
 * no data, and TanStack Query counts a query without data as stale whatever
 * its `staleTime`, so each focus would re-parse it and put it back to
 * pending, which takes down whatever is waiting on it. A new mount still
 * retries a failure, so reloading the page re-parses the query.
 */
export function useRoboqlSpec(
  query: string | undefined,
  orgId: string | undefined,
) {
  const { queryService } = useDomainServices();
  return useQuery<SearchQueryBody, Error>({
    queryKey: ["roboqlSpec", orgId, query],
    queryFn: ({ signal }) =>
      queryService.convertRoboqlToSpec(query ?? "", {
        abortSignal: signal,
        orgId,
      }),
    enabled: query !== undefined,
    staleTime: Infinity,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
